export enum ExchangeTypes {
    Permanent = "Permanent",
    Temporary = "Temporary",
    PriceChange = "PriceChange",
    // A DAMS Cross Hire is a temporary exchange with DAMS specific vehicle statuses.
    // The exchange back into the contracted vehicle is an ordinary Permanent exchange.
    DAMS_Cross_Hire = "DAMS_Cross_Hire"
}

export enum ExchangeReasons {
    AccidentReplacement = "AccidentReplacement",
    DefleetingVehicle = "DefleetingVehicle",
    ReturningToContractedVehicle = "ReturningToContractedVehicle",
    TotalLossReplacement = "TotalLossReplacement",
    WarrantyReplacement = "WarrantyReplacement",
    SoldAsPreloved = "SoldAsPreloved",
    OutrightPurchase = "OutrightPurchase",
    PCOExpired = "PCOExpired",
    InterstateTransfer = "InterstateTransfer",
    VehicleUpgrade = "VehicleUpgrade"
}

// Exchange types that put the customer into a replacement vehicle they must later hand back.
// These need a contracted vehicle to return to, and the replacement always comes from the
// Flexi fleet. Ask this rather than comparing to a single type, so a new exchange type of the
// same shape cannot silently fall through.
const REPLACEMENT_VEHICLE_EXCHANGE_TYPES: ExchangeTypes[] = [
    ExchangeTypes.Temporary,
    ExchangeTypes.DAMS_Cross_Hire,
];

export const isReplacementVehicleExchange = (exchangeType: string): boolean =>
    REPLACEMENT_VEHICLE_EXCHANGE_TYPES.includes(exchangeType as ExchangeTypes);

export type ExchangeReasonState = {
  exchangeType: ExchangeTypes;
  // Empty until the user picks a reason - there is no sensible default
  exchangeReason: ExchangeReasons | "";
  comments: string;
  // Price change only - the operator must confirm they understand the scope before continuing
  priceChangeAcknowledged: boolean;
};

export type ExchangeReasonAction =
  // Payload is the raw dropdown string - isExchangeType below narrows it before it reaches state
  | { type: "USER_SELECTS_EXCHANGE_TYPE"; payload: string }
  | { type: "USER_SELECTS_EXCHANGE_REASON"; payload: ExchangeReasons }
  | { type: "USER_TYPES_COMMENTS"; payload: string }
  | { type: "USER_ACKNOWLEDGES_PRICE_CHANGE"; payload: boolean };

export const exchangeReasonInitialState: ExchangeReasonState = {
  exchangeType: ExchangeTypes.Permanent,
  exchangeReason: "",
  comments: "",
  priceChangeAcknowledged: false,
};

// Type guard - the payload arrives as a raw string from the dropdown
const isExchangeType = (value: string): value is ExchangeTypes =>
    Object.values(ExchangeTypes).includes(value as ExchangeTypes);

export function exchangeReasonReducer(state: ExchangeReasonState, action: ExchangeReasonAction): ExchangeReasonState {
  switch (action.type) {
    case "USER_SELECTS_EXCHANGE_TYPE":
      if (!isExchangeType(action.payload)) {
        // Don't guess at an exchange type - the wrong one silently performs the wrong exchange
        console.error(`Error - unknown exchange type selected: ${action.payload}`);
        return state;
      }
      // The available reasons depend on the type, so any existing selection may no longer be valid.
      // The acknowledgement is specific to a price change, so it cannot carry over either.
      return { ...state, exchangeType: action.payload, exchangeReason: "", priceChangeAcknowledged: false };
    case "USER_SELECTS_EXCHANGE_REASON":
      return { ...state, exchangeReason: action.payload };
    case "USER_TYPES_COMMENTS":
      return { ...state, comments: action.payload };
    case "USER_ACKNOWLEDGES_PRICE_CHANGE":
      return { ...state, priceChangeAcknowledged: action.payload };
    default:
      console.log(`Error - incorrect action sent ${action} ${state}`)
      return state;
  }
}
